<template>
  <table
    role="grid"
    :aria-label="t('el.datepicker.dateTablePrompt')"
    cellspacing="0"
    cellpadding="0"
    :class="[
      ns.b(),
      {
        'is-week-mode':
          selectionMode === 'week' || selectionMode === 'week-range',
      },
    ]"
    @click="handlePickDate"
    @mousemove="handleMouseMove"
    @mousedown.prevent="handleMouseDown"
    @mouseup="handleMouseUp"
  >
    <tbody ref="tbodyRef">
      <tr>
        <th v-if="showWeekNumber" scope="col">{{ t('el.datepicker.week') }}</th>
        <th
          v-for="(week, key) in WEEKS"
          :key="key"
          scope="col"
          :aria-label="t('el.datepicker.weeksFull.' + week)"
        >
          {{ t('el.datepicker.weeks.' + week) }}
        </th>
      </tr>
      <tr
        v-for="(row, rowKey) in rows"
        :key="rowKey"
        :class="[ns.e('row'), { current: isWeekActive(row[1]) }]"
      >
        <td
          v-for="(cell, columnKey) in row"
          :key="`${rowKey}.${columnKey}`"
          :ref="(el) => isSelectedCell(cell) && (currentCellRef = el as HTMLElement)"
          :class="getCellClasses(cell)"
          :aria-current="cell.isCurrent ? 'date' : undefined"
          :aria-selected="cell.isCurrent"
          :aria-disabled="cell.disabled"
          :tabindex="isSelectedCell(cell) ? 0 : -1"
          data-component-name="DatePicker"
          :data-event-name="
            selectionMode === 'week' || selectionMode === 'week-range'
              ? 'week'
              : 'day'
          "
          :data-event-content="
            cell.dayjs
              ? cell.dayjs.format('YYYY-MM-DD')
              : String(cell.text || '')
          "
          @focus="handleFocus"
        >
          <jd-date-picker-cell :cell="cell" />
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, unref, watch } from 'vue'
import dayjs from 'dayjs'
import { flatten } from 'lodash-unified'
import { useLocale, useNamespace, useWeekStart } from '@jdesign-vue/hooks'
import { castArray } from '@jdesign-vue/utils'
import {
  getWeekEndDay,
  getWeekNumber,
  getWeekStartDay,
  getWeekYear,
} from '@jdesign-vue/utils/week'
import { basicDateTableProps } from '../props/basic-date-table'
import { buildPickerTable } from '../utils'
import JdDatePickerCell from './basic-cell-render'

import type { Dayjs } from 'dayjs'
import type { DateCell } from '../date-picker.type'

const props = defineProps(basicDateTableProps)
const emit = defineEmits(['changerange', 'pick', 'select'])

const ns = useNamespace('date-table')

const { t, lang } = useLocale()

const tbodyRef = ref<HTMLElement>()
const currentCellRef = ref<HTMLElement>()
// data
const lastRow = ref<number>()
const lastColumn = ref<number>()
const tableRows = ref<DateCell[][]>([[], [], [], [], [], []])

let focusWithClick = false

const weekStartValue = useWeekStart()
// firstDayOfWeek: 1-7 for internal calculation (7 means Sunday)
const firstDayOfWeek = computed(() =>
  weekStartValue.value === 0 ? 7 : weekStartValue.value
)
const WEEKS_CONSTANT = props.date
  .locale('en')
  .localeData()
  .weekdaysShort()
  .map((_) => _.toLowerCase())

// offsetDay 已不再需要：startDate 已直接计算了日历网格的第一天

const startDate = computed(() => {
  const startDayOfMonth = props.date.startOf('month')
  // 计算月首日相对于周首日的偏移
  const dayOfWeek = startDayOfMonth.day() // 0=Sunday, 1=Monday, ...
  const fdow = firstDayOfWeek.value // 1-7, 7=Sunday
  const fdow0 = fdow === 7 ? 0 : fdow // 转为 0-6
  const daysFromWeekStart = (dayOfWeek - fdow0 + 7) % 7 || 7
  return startDayOfMonth.subtract(daysFromWeekStart, 'day')
})

const WEEKS = computed(() => {
  return WEEKS_CONSTANT.concat(WEEKS_CONSTANT).slice(
    firstDayOfWeek.value,
    firstDayOfWeek.value + 7
  )
})

const hasCurrent = computed<boolean>(() => {
  return flatten(rows.value).some((row) => {
    return row.isCurrent
  })
})

const currentMonth = computed(() => props.date.month())

const selectedDate = computed(() => {
  return props.selectionMode === 'dates'
    ? (castArray(props.parsedValue) as Dayjs[])
    : ([] as Dayjs[])
})

// 基于 cell.dayjs（已在 buildPickerTable 中设置）判断 prev/next month 并设置显示文本
const setDateText = (cell: DateCell): void => {
  const cellDate = cell.dayjs!
  cell.text = cellDate.date()
  const cellMonth = cellDate.month()
  const month = unref(currentMonth)
  if (cellMonth !== month) {
    // 判断是上个月还是下个月（处理年跨界：12月看1月 / 1月看12月）
    cell.type =
      (month - cellMonth + 12) % 12 === 1 ? 'prev-month' : 'next-month'
  }
}

const isWithinRangeLimit = (date: Dayjs): boolean => {
  if (!props.rangeLimit || !props.minDate) return true

  // 只在日期范围模式下应用天数限制
  if (props.selectionMode !== 'range') return true

  // 计算从minDate开始的天数差（包括开始日期）
  const diffDays = Math.abs(date.diff(props.minDate, 'day')) + 1
  return diffDays <= props.rangeLimit
}

const setCellMetadata = (
  cell: DateCell,
  _dimension: { columnIndex: number; rowIndex: number }
) => {
  const { disabledDate, cellClassName } = props
  const _selectedDate = unref(selectedDate)
  setDateText(cell)

  const cellDate = cell.dayjs!.toDate()
  cell.selected = _selectedDate.find(
    (d) => d.valueOf() === cell.dayjs!.valueOf()
  )
  cell.isSelected = !!cell.selected
  cell.isCurrent = isCurrent(cell)

  // 检查是否超出范围限制
  let isOutOfRangeLimit = false
  if (
    props.selectionMode === 'range' &&
    props.rangeLimit &&
    props.minDate &&
    props.rangeState.selecting
  ) {
    isOutOfRangeLimit = !isWithinRangeLimit(cell.dayjs!)
  }

  cell.disabled = disabledDate?.(cellDate) || isOutOfRangeLimit
  cell.customClass = cellClassName?.(cellDate)
}

const setRowMetadata = (row: DateCell[]) => {
  if (props.selectionMode === 'week') {
    const [start, end] = props.showWeekNumber ? [1, 7] : [0, 6]
    const isActive = isWeekActive(row[start + 1])
    row[start].inRange = isActive
    row[start].start = isActive
    row[end].inRange = isActive
    row[end].end = isActive
  }
}

const rows = computed(() => {
  const { minDate, maxDate, rangeState, showWeekNumber } = props

  const rows_ = tableRows.value
  const dateUnit = 'day'

  if (showWeekNumber) {
    for (let rowIndex = 0; rowIndex < 6; rowIndex++) {
      const weekDate = startDate.value.add(rowIndex * 7, dateUnit)
      rows_[rowIndex][0] = {
        type: 'week',
        text: getWeekNumber(weekDate, weekStartValue.value),
      }
    }
  }

  // 周范围反向 hover 时，将 startDate 扩展到首次点击周的末尾
  // 以确保高亮覆盖完整的首次选中周
  const effectiveStartDate =
    props.selectionMode === 'week-range' &&
    minDate &&
    rangeState.endDate &&
    rangeState.endDate.isBefore(minDate)
      ? getWeekEndDay(minDate, weekStartValue.value)
      : minDate

  buildPickerTable({ row: 6, column: 7 }, rows_, {
    startDate: effectiveStartDate,
    columnIndexOffset: showWeekNumber ? 1 : 0,
    nextEndDate:
      rangeState.endDate ||
      maxDate ||
      (rangeState.selecting && minDate) ||
      null,
    now: dayjs().locale(unref(lang)).startOf(dateUnit),
    unit: dateUnit,
    relativeDateGetter: (idx: number) => startDate.value.add(idx, dateUnit),
    setCellMetadata,

    setRowMetadata,
  })

  return rows_
})

watch(
  () => props.date,
  async () => {
    if (tbodyRef.value?.contains(document.activeElement)) {
      await nextTick()
      currentCellRef.value?.focus()
    }
  }
)

const focus = async () => {
  currentCellRef.value?.focus()
}

const isNormalDay = (type = '') => {
  return ['normal', 'today'].includes(type)
}

const isCurrent = (cell: DateCell): boolean => {
  return (
    props.selectionMode === 'date' &&
    isNormalDay(cell.type) &&
    cellMatchesDate(cell, props.parsedValue as Dayjs)
  )
}

const cellMatchesDate = (cell: DateCell, date: Dayjs) => {
  if (!date) return false
  return dayjs(date)
    .locale(lang.value)
    .isSame(props.date.date(Number(cell.text)), 'day')
}

const getCellClasses = (cell: DateCell) => {
  const classes: string[] = []
  if (isNormalDay(cell.type) && !cell.disabled) {
    classes.push('available')
    if (cell.type === 'today') {
      classes.push('today')
    }
  } else {
    classes.push(cell.type!)
  }

  if (isCurrent(cell)) {
    classes.push('current')
  }

  if (
    cell.inRange &&
    (isNormalDay(cell.type) ||
      props.selectionMode === 'week' ||
      props.selectionMode === 'week-range')
  ) {
    classes.push('in-range')

    // week-range 模式下 start/end 标记仅在当月单元格显示，
    // 避免 prev/next-month 溢出格在另一面板重复出现起止标记
    const showMarkers =
      props.selectionMode !== 'week-range' || isNormalDay(cell.type)

    if (cell.start && showMarkers) {
      classes.push('start-date')
    }

    if (cell.end && showMarkers) {
      classes.push('end-date')
    }
  }

  if (cell.disabled) {
    classes.push('disabled')
  }

  if (cell.selected) {
    classes.push('selected')
  }

  if (cell.customClass) {
    classes.push(cell.customClass)
  }

  return classes.join(' ')
}

const getDateOfCell = (row: number, column: number) => {
  const offsetFromStart = row * 7 + (column - (props.showWeekNumber ? 1 : 0))
  return startDate.value.add(offsetFromStart, 'day')
}

const handleMouseMove = (event: MouseEvent) => {
  if (!props.rangeState.selecting) return

  let target = event.target as HTMLElement
  if (target.tagName === 'SPAN') {
    target = target.parentNode?.parentNode as HTMLElement
  }
  if (target.tagName === 'DIV') {
    target = target.parentNode as HTMLElement
  }
  if (target.tagName !== 'TD') return

  const row = (target.parentNode as HTMLTableRowElement).rowIndex - 1
  const column = (target as HTMLTableCellElement).cellIndex

  // can not select disabled date
  if (rows.value[row][column].disabled) return

  const targetDate = getDateOfCell(row, column)

  // 检查范围限制（只在日期范围模式下应用）
  if (props.rangeLimit && props.minDate && props.selectionMode === 'range') {
    const diffDays = Math.abs(targetDate.diff(props.minDate, 'day')) + 1
    if (diffDays > props.rangeLimit) {
      return
    }
  }

  // only update rangeState when mouse moves to a new cell
  // this avoids frequent Date object creation and improves performance
  if (row !== lastRow.value || column !== lastColumn.value) {
    lastRow.value = row
    lastColumn.value = column
    let endDate = getDateOfCell(row, column)
    // 周范围模式下，根据 hover 方向对齐到周边界
    if (props.selectionMode === 'week-range') {
      if (props.minDate && endDate.isBefore(props.minDate)) {
        // 反向选择：对齐到周起始，使反向检查覆盖整周
        endDate = getWeekStartDay(endDate, weekStartValue.value)
      } else {
        endDate = getWeekEndDay(endDate, weekStartValue.value)
      }
    }
    emit('changerange', {
      selecting: true,
      endDate,
    })
  }
}

const isSelectedCell = (cell: DateCell) => {
  return (
    (!hasCurrent.value && cell?.text === 1 && cell.type === 'normal') ||
    cell.isCurrent
  )
}

const handleFocus = (event: FocusEvent) => {
  if (focusWithClick || hasCurrent.value || props.selectionMode !== 'date')
    return
  handlePickDate(event, true)
}

const handleMouseDown = (event: MouseEvent) => {
  const target = (event.target as HTMLElement).closest('td')
  if (!target) return
  focusWithClick = true
}

const handleMouseUp = (event: MouseEvent) => {
  const target = (event.target as HTMLElement).closest('td')
  if (!target) return
  focusWithClick = false
}

const handlePickDate = (
  event: FocusEvent | MouseEvent,
  isKeyboardMovement = false
) => {
  const target = (event.target as HTMLElement).closest('td')

  if (!target) return

  const row = (target.parentNode as HTMLTableRowElement).rowIndex - 1
  const column = (target as HTMLTableCellElement).cellIndex
  const cell = rows.value[row][column]

  if (cell.disabled || cell.type === 'week') return

  const newDate = getDateOfCell(row, column)

  if (props.selectionMode === 'range') {
    if (!props.rangeState.selecting || !props.minDate) {
      emit('pick', { minDate: newDate, maxDate: null })
      emit('select', true)
    } else {
      // 检查范围限制（只在日期范围模式下应用）
      if (props.rangeLimit && props.selectionMode === 'range') {
        const diffDays = Math.abs(newDate.diff(props.minDate, 'day')) + 1
        if (diffDays > props.rangeLimit) {
          // 如果超出范围限制，不允许选择
          return
        }
      }

      if (newDate >= props.minDate) {
        emit('pick', { minDate: props.minDate, maxDate: newDate })
      } else {
        emit('pick', { minDate: newDate, maxDate: props.minDate })
      }
      emit('select', false)
    }
  } else if (props.selectionMode === 'week-range') {
    // 周范围模式：将选中日期扩展到整周边界
    const ws = weekStartValue.value
    const weekStartDay = getWeekStartDay(newDate, ws)
    const weekEndDay = getWeekEndDay(newDate, ws)
    if (!props.rangeState.selecting || !props.minDate) {
      emit('pick', { minDate: weekStartDay, maxDate: null })
      emit('select', true)
    } else {
      if (weekStartDay >= props.minDate) {
        emit('pick', { minDate: props.minDate, maxDate: weekEndDay })
      } else {
        emit('pick', {
          minDate: weekStartDay,
          maxDate: getWeekEndDay(props.minDate, ws),
        })
      }
      emit('select', false)
    }
  } else if (props.selectionMode === 'date') {
    emit('pick', newDate, isKeyboardMovement)
  } else if (props.selectionMode === 'week') {
    const ws = weekStartValue.value
    const weekNumber = getWeekNumber(newDate, ws)
    const weekYear = getWeekYear(newDate, ws)
    const value = `${weekYear}w${weekNumber}`
    emit('pick', {
      year: weekYear,
      week: weekNumber,
      value,
      date: getWeekStartDay(newDate, ws),
    })
  } else if (props.selectionMode === 'dates') {
    const newValue = cell.selected
      ? castArray(props.parsedValue).filter(
          (d) => d?.valueOf() !== newDate.valueOf()
        )
      : castArray(props.parsedValue).concat([newDate])
    emit('pick', newValue)
  }
}

const isWeekActive = (cell: DateCell) => {
  if (props.selectionMode !== 'week') return false
  let newDate = props.date.startOf('day')

  if (cell.type === 'prev-month') {
    newDate = newDate.subtract(1, 'month')
  }

  if (cell.type === 'next-month') {
    newDate = newDate.add(1, 'month')
  }

  newDate = newDate.date(Number.parseInt(cell.text as any, 10))

  if (props.parsedValue && !Array.isArray(props.parsedValue)) {
    const dayOffset =
      ((props.parsedValue.day() - firstDayOfWeek.value + 7) % 7) - 1
    const weekDate = props.parsedValue.subtract(dayOffset, 'day')
    return weekDate.isSame(newDate, 'day')
  }
  return false
}

defineExpose({
  /**
   * @description focus on current cell
   */
  focus,
})
</script>
